import { Card, CardBody, Col, Container, Input, Label, Row } from "reactstrap";
import DataTable, { TableColumn } from "react-data-table-component";
import { useEffect, useMemo, useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
} from "reactstrap";
import { SearchTableButton } from "../../../../utils/Constant";
import RfidCardListFilterHeader from "./RfidCardListFilterHeader";
import {
  RfidCardListTableOfferComponent,
  RfidCardListTableStatus,
} from "../../../../Types/Application/Ecommerce/RfidCardList";
import api from "../../../../utils/api";
import Swal from "sweetalert2";
import { RfidCardListTableDataColumnType } from "../../../../Types/Application/Ecommerce/Ecommerce";
import { SVG } from "../../../../AbstractElements";

const RfidCardListContainer = () => {
  const [filterText, setFilterText] = useState("");
  const [RfidCardListTableDatas, setRfidCardListTableDatas] = useState<RfidCardListTableDataColumnType[]>([]);
  
  // ⬇️ PAGINATION STATE VARIABLES
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading] = useState(false);
  
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editData, setEditData] = useState<any>(null);

  // ⬇️ UPDATED FETCH FUNCTION WITH PAGINATION
  const fetchCardLists = async (page: number, size: number) => {
    try {
      setLoading(true);
      const response = await api.get(
        `/api/RFIDCardMaster/GetRFIDCardMasterPagination?pageNumber=${page}&pageSize=${size}`
      );

      if (response.status === 200) {
        // ⬇️ response.data.items use karein (assuming same structure)
        setRfidCardListTableDatas(
          response.data.items.map(
            (item: {
              rfId: any;
              rfidCardUniqueNumber: any;
              cardAssigns: any;
              status: any;
            }) => ({
              rfId: item.rfId,
              rfid_card_no: item.rfidCardUniqueNumber,
              cardAssigns: item.cardAssigns,
              card_status: item.status ? "Active" : "Inactive",
            })
          )
        );
        setTotalRows(response.data.totalCount); // ⬅️ Total count set karein
      }
    } catch (error) {
      console.error("Failed to fetch RFID Card list:", error);
      Swal.fire("Error!", "Failed to fetch RFID Card list.", "error");
    } finally {
      setLoading(false);
    }
  };

  // ⬇️ USEEFFECT WITH PAGINATION DEPENDENCIES
  useEffect(() => {
    fetchCardLists(currentPage, pageSize);
  }, [currentPage, pageSize]);

  const handleDelete = async (id: number) => {
    Swal.fire({
      title: 'Are you sure?',
      text: 'You will not be able to recover this RFID Card!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
    }).then((result) => {
      if (result.isConfirmed) {
        confirmDelete(id);
      }
    });
  };

  const confirmDelete = async (id: number) => {
    try {
      await api.delete(`/api/RFIDCardMaster/DeleteRFIDCardMaster?rfid=${id}`);
      Swal.fire("Deleted!", "RFID Card has been deleted.", "success");

      // ⬇️ REFRESH CURRENT PAGE
      await fetchCardLists(currentPage, pageSize);
    } catch (error) {
      Swal.fire("Error!", "Failed to delete the RFID Card.", "error");
      console.error("Delete error:", error);
    }
  };

  const handleEdit = (row: any) => {
    setEditData(row);
    setEditModalOpen(true);
  };

  const handleEditChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditData({ ...editData, [e.target.name]: e.target.value });
  };

  const handleEditSave = async () => {
    if (!editData || !editData.rfId || !editData.rfid_card_no) {
      Swal.fire("Error!", "Please fill all fields before saving.", "error");
      return;
    }
    if (editData.rfid_card_no.trim() === "") {
      Swal.fire("Error!", "RFID Card number cannot be empty.", "error");
      return;
    }
    if (editData.rfid_card_no !== undefined && isNaN(Number(editData.rfid_card_no))) {
      Swal.fire("Error!", "Please select a valid card number.", "error");
      return;
    }
    if (editData.rfid_card_no.length !== 10) {
      Swal.fire("Error!", "RFID Card number must be exactly 10 digits long.", "error");
      return;
    }

    try {
      await api.put(`/api/RFIDCardMaster/UpdateRFIDCardMaster`, {
        rfId: editData.rfId,
        rfidCardUniqueNumber: editData.rfid_card_no,
        status: editData.card_status === "Active" ? true : false,
      });
      
      // ⬇️ REFRESH CURRENT PAGE INSTEAD OF LOCAL STATE UPDATE
      await fetchCardLists(currentPage, pageSize);
      
      setEditModalOpen(false);
      Swal.fire("Updated!", "RFID Card number has been updated.", "success");
    } catch (error) {
      Swal.fire("Error!", "Failed to update the Record.", "error");
    }
  };

  const RfidCardListTableDataColumn: TableColumn<RfidCardListTableDataColumnType>[] = [
    {
      name: "RFID Card No.",
      cell: (row) => <RfidCardListTableOfferComponent title={String(row.rfid_card_no)} />,
      sortable: true,
      grow: 2,
    },
    {
      name: "Card Status",
      cell: (row) => <RfidCardListTableStatus title={row.card_status} />,
    },
    {
      name: "Action",
      cell: (row) => (
        <div className="d-flex align-items-center gap-2">
          <span
            onClick={() => handleEdit(row)}
            title="Edit"
            style={{ cursor: "pointer" }}
          >
            <SVG
              iconId="edit-content"
              style={{ fill: "blue", width: 18, height: 18 }}
            />
          </span>
          <span
            onClick={() => handleDelete(Number(row.rfId))}
            title="Delete"
            style={{ cursor: "pointer" }}
          >
            <SVG
              iconId="trash1"
              style={{ fill: "red", width: 18, height: 18 }}
            />
          </span>
        </div>
      ),
    },
  ];

  // ⬇️ LOCAL SEARCH ON CURRENT PAGE DATA (optional)
  const filteredItems = RfidCardListTableDatas.filter((item: any) =>
    Object.values(item)
      .join(' ')
      .toLowerCase()
      .includes(filterText.toLowerCase())
  );

  const subHeaderComponentMemo = useMemo(() => {
    return (
      <div className="dataTables_filter d-flex align-items-center">
        <Label className="me-2">{SearchTableButton}:</Label>
        <Input
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setFilterText(e.target.value)
          }
          type="search"
          value={filterText}
        />
      </div>
    );
  }, [filterText]);

  // ⬇️ PAGE CHANGE HANDLERS
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePerRowsChange = (newPerPage: number, page: number) => {
    setPageSize(newPerPage);
    setCurrentPage(page);
  };

  return (
    <Container fluid>
      <Row>
        <Col sm="12">
          <Card>
            <CardBody>
              <div className="list-product-header">
                <RfidCardListFilterHeader />
              </div>
              <div className="list-product">
                <div className="table-responsive">
                  {/* ⬇️ UPDATED DATATABLE WITH SERVER PAGINATION */}
                  <DataTable
                    data={filteredItems} // Ya direct RfidCardListTableDatas if no local search
                    columns={RfidCardListTableDataColumn}
                    striped
                    highlightOnHover
                    pagination
                    paginationServer // ⬅️ SERVER-SIDE PAGINATION ENABLE
                    paginationTotalRows={totalRows} // ⬅️ TOTAL ROWS FROM API
                    onChangePage={handlePageChange} // ⬅️ PAGE CHANGE HANDLER
                    onChangeRowsPerPage={handlePerRowsChange} // ⬅️ ROWS PER PAGE HANDLER
                    paginationDefaultPage={currentPage} // ⬅️ CURRENT PAGE
                    paginationPerPage={pageSize} // ⬅️ PAGE SIZE
                    progressPending={loading} // ⬅️ LOADING INDICATOR
                    selectableRows
                    subHeader
                    subHeaderComponent={subHeaderComponentMemo}
                  />
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
      
      <Modal
        isOpen={editModalOpen}
        toggle={() => setEditModalOpen(!editModalOpen)}
      >
        <ModalHeader toggle={() => setEditModalOpen(!editModalOpen)}>
          Edit Rfid
        </ModalHeader>
        <ModalBody>
          {editData && (
            <>
              <FormGroup>
                <Label>Rfid No</Label>
                <Input
                  name="rfid_card_no"
                  value={editData.rfid_card_no}
                  onChange={handleEditChange}
                />
              </FormGroup>
              <FormGroup>
                <Label>Status</Label>
                <Input
                  type="select"
                  name="card_status"
                  value={editData.card_status}
                  onChange={handleEditChange}
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </Input>
              </FormGroup>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setEditModalOpen(false)}>
            Cancel
          </Button>
          <Button color="primary" onClick={handleEditSave}>
            Save
          </Button>
        </ModalFooter>
      </Modal>
    </Container>
  );
};

export default RfidCardListContainer;